import React, { useContext, useEffect, useRef, useState } from "react";
import "./Calendar.css";
import { useLanguage } from "../../LanguageProvider";
import { TbEdit, TbPlayerPlay, TbPlayerStop } from "react-icons/tb";
import Dropdown from "../Dropdown/Dropdown";
import { TbClipboardText } from "react-icons/tb";
import TodaysTaskModal from "./components/TodaysTaskModal/TodaysTaskModal";
import AlertBox from "../AlertBox/AlertBox";
import AddTaskModal from "../../pages/TaskManagementPage/AddTaskModal/AddTaskModal";
import TaskDetailsModal from "./components/TaskDetailsModal/TaskDetailsModal";
import { GeneralContext } from "../../GeneralProvider";
import { useToaster } from "../../ToasterProvider";

const Calendar = ({
  panels,
  board,
  selectedPanelId,
  propTasks,
  setSelectedSubmenu,
  setSelectedPanelId,
  projectAuthorization,
  autoTask,
}) => {
  const { toastError } = useToaster();
  const { getTranslation } = useLanguage();
  const {
    handleDeleteBoard,
    handleBoardUpdate,
    setPanelSelectedYear,
    setPanelSelectedMonth,
    handleKronosCreateAndStart,
    handleKronosDiscard,
    handleKronosStop,
    activeKronos,
    triggerKronosRefresh
  } = useContext(GeneralContext);
  const calendarRef = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [boardTitle, setBoardTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedTask, setSelectedTask] = useState(null);

  const [isTodaysTaskModalOpen, setIsTodaysTaskModalOpen] = useState(false);
  const [todayTasks, setTodayTasks] = useState([]);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isKronosAlertOpen, setIsKronosAlertOpen] = useState(false);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [isTaskDetailsModalOpen, setIsTaskDetailsModalOpen] = useState(false);

  const [selectedKronosTask, setSelectedKronosTask] = useState(null);

  const monthNames = [
    getTranslation("january"),
    getTranslation("february"),
    getTranslation("march"),
    getTranslation("april"),
    getTranslation("may"),
    getTranslation("june"),
    getTranslation("july"),
    getTranslation("august"),
    getTranslation("september"),
    getTranslation("october"),
    getTranslation("november"),
    getTranslation("december"),
  ];

  const monthNamesDDL = [
    { value: 0, label: getTranslation("january") },
    { value: 1, label: getTranslation("february") },
    { value: 2, label: getTranslation("march") },
    { value: 3, label: getTranslation("april") },
    { value: 4, label: getTranslation("may") },
    { value: 5, label: getTranslation("june") },
    { value: 6, label: getTranslation("july") },
    { value: 7, label: getTranslation("august") },
    { value: 8, label: getTranslation("september") },
    { value: 9, label: getTranslation("october") },
    { value: 10, label: getTranslation("november") },
    { value: 11, label: getTranslation("december") },
  ];

  const yearsDDL = Array.from({ length: 21 }, (_, i) => {
    const year = new Date().getFullYear() - 10 + i;
    return { value: year, label: year.toString() };
  });

  const dayNames = [
    getTranslation("sunday"),
    getTranslation("monday"),
    getTranslation("tuesday"),
    getTranslation("wednesday"),
    getTranslation("thursday"),
    getTranslation("friday"),
    getTranslation("saturday"),
  ];

  const parseDateString = (dateString) => {
    return new Date(dateString?.replace(" ", "T"));
  };

  const daysInMonth = [];
  const daysInSelectedMonth = new Date(
    selectedYear,
    selectedMonth + 1,
    0
  ).getDate();
  for (let day = 1; day <= daysInSelectedMonth; day++) {
    const date = new Date(selectedYear, selectedMonth, day);
    const dayName = dayNames[date.getDay()];
    daysInMonth.push({
      date: day,
      dayName: dayName,
      fullDate: date,
    });
  }

  const handleCreateStartKronos = async (bypassCheck = false, task) => {
    if (!bypassCheck && activeKronos) {
      setIsKronosAlertOpen(true);
      setSelectedKronosTask(task);
      return;
    }
    const kronosData = { title: task.taskTitle, taskId: task.taskId };
    try {
      const result = await handleKronosCreateAndStart(kronosData);
      if (result.status) {
        triggerKronosRefresh();
      } else {
        toastError(getTranslation("somethingWentWrong"));
      }
    } catch (error) {
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const cancelPreviousAndStart = async () => {
    try {
      const result = await handleKronosDiscard(
        activeKronos?.kronosId,
        activeKronos?.logId
      );
      if (result?.status) {
        handleCreateStartKronos(true, selectedKronosTask);
        setIsKronosAlertOpen(false);
      }
    } catch (error) {
      console.error("Error in cancelPreviousAndStart:", error);
    }
  };

  const stopKronos = async () => {
    try {
      await handleKronosStop(activeKronos?.logId);
    } catch (error) {
      console.error("Error in savePreviousAndStart:", error);
    }
  }

  const savePreviousAndStart = async () => {
    try {
      const result = await handleKronosStop(activeKronos?.logId);
      if (result?.status) {
        handleCreateStartKronos(true, selectedKronosTask);
        setIsKronosAlertOpen(false);
      }
    } catch (error) {
      console.error("Error in savePreviousAndStart:", error);
    }
  };

  const buttons = [
    {
      text: getTranslation("savePreviousAndStart"),
      onClick: savePreviousAndStart,
      styleType: "alert-confirm",
    },
    {
      text: getTranslation("cancelPreviousAndStart"),
      onClick: cancelPreviousAndStart,
      styleType: "alert-confirm",
    },
    {
      text: getTranslation("close"),
      onClick: () => setIsKronosAlertOpen(false),
      styleType: "alert-cancel",
    },
  ];

  useEffect(() => {
    if (isTaskDetailsModalOpen || isNewTaskModalOpen) {
      return;
    }

    const today = new Date();
    const todayDateString = today.toISOString().split("T")[0];

    const storedDate = localStorage.getItem("dontShowDate");
    const dontShowToday = localStorage.getItem("dontShowToday") === "true";

    if (storedDate === todayDateString && dontShowToday) {
      return;
    }

    const tasksForToday = tasks.filter((task) => {
      const taskStartDate = parseDateString(task.taskStart);
      const taskEndDate = parseDateString(task.taskEnd);

      return (
        ((today >= taskStartDate && today <= taskEndDate) ||
          taskStartDate.toDateString() === todayDateString ||
          taskEndDate.toDateString() === todayDateString) && task.taskStatus !== "1" && task.taskStatus !== "2"
      );
    });

    if (tasksForToday.length > 0) {
      setTodayTasks(tasksForToday);
      setIsTodaysTaskModalOpen(true);
    }
  }, [tasks]);

  useEffect(() => {
    if (autoTask) {
      const taskDate = new Date(autoTask.taskStart);
      setSelectedYear(taskDate.getFullYear());
      setSelectedMonth(taskDate.getMonth());
      openModalWithTask(autoTask);
    }
  }, [autoTask]);


  const tasksInMonth = tasks?.filter((task) => {
    const taskStartDate = parseDateString(task.taskStart);
    const taskEndDate = parseDateString(task.taskEnd);

    const taskStartMonth = taskStartDate.getMonth();
    const taskEndMonth = taskEndDate.getMonth();

    const taskStartYear = taskStartDate.getFullYear();
    const taskEndYear = taskEndDate.getFullYear();

    return (
      (taskStartYear === selectedYear || taskEndYear === selectedYear) &&
      (taskStartMonth === selectedMonth || taskEndMonth === selectedMonth)
    );
  });

  const handleTitleUpdate = async () => {
    setIsEditing(false);
    if (!boardTitle.trim()) return;

    try {
      const updateData = {
        title: boardTitle.trim(),
      };

      await handleBoardUpdate(board.id, updateData);
    } catch (error) {
      console.error("Başlık güncelleme hatası:", error);
      // toastError(getTranslation("somethingWentWrong"));
    }
  };

  useEffect(() => {
    setPanelSelectedYear(new Date().getFullYear())
    setPanelSelectedMonth(new Date().getMonth())
  }, []);

  const handleConfirmDeleteBoard = () => {
    setSelectedSubmenu(null);
    setSelectedPanelId("");
    setIsAlertOpen(false);
    handleDeleteBoard(board.id);
  };

  const handleCancelDeleteBoard = () => {
    setIsAlertOpen(false);
  };

  const openModalWithTask = (task, ref) => {
    setSelectedTask(task);
    setIsTaskDetailsModalOpen(true);
  };

  const closeTaskDetailsModal = () => {
    setSelectedTask(null);
    setIsTaskDetailsModalOpen(false);
  };
  const openTodayTasksModal = () => {
    const today = new Date();
    const todayDateString = today.toDateString();
    const tasksForToday = tasks.filter((task) => {
      const taskStartDate = new Date(task.taskStart);
      const taskEndDate = new Date(task.taskEnd);

      // Bugünün tarihi taskStart ve taskEnd arasında mı veya taskStart'a ya da taskEnd'e eşit mi?
      return (
        ((today >= taskStartDate && today <= taskEndDate) ||
          taskStartDate.toDateString() === todayDateString ||
          taskEndDate.toDateString() === todayDateString) && task.taskStatus !== "1" && task.taskStatus !== "2"
      );
    });
    setTodayTasks(tasksForToday);
    setIsTodaysTaskModalOpen(true);
  };

  const closeTodayTasksModal = () => {
    setIsTodaysTaskModalOpen(false);
  };

  const openNewTaskModal = () => {
    setIsNewTaskModalOpen(true);
  };

  const closeNewTaskModal = () => {
    setIsNewTaskModalOpen(false);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(parseInt(event.target.value));
    setPanelSelectedMonth(parseInt(event.target.value));
  };

  const handleYearChange = (event) => {
    setSelectedYear(parseInt(event.target.value));
    setPanelSelectedYear(parseInt(event.target.value));
  };

  useEffect(() => {
    const calendarContainer = calendarRef.current;
    let isDown = false;
    let startX;
    let scrollLeft;

    const handleMouseDown = (e) => {
      if (e.target.closest(".task-detail-modal-main-container")) return;
      isDown = true;
      calendarContainer.classList.add("active");
      startX = e.pageX - calendarContainer.offsetLeft;
      scrollLeft = calendarContainer.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      calendarContainer.classList.remove("active");
    };

    const handleMouseUp = (e) => {
      if (e.target.closest(".task-detail-modal-main-container")) return;
      isDown = false;
      calendarContainer.classList.remove("active");
    };

    const handleMouseMove = (e) => {
      if (e.target.closest(".task-detail-modal-main-container")) return;
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - calendarContainer.offsetLeft;
      const walk = (x - startX) * 2;
      calendarContainer.scrollLeft = scrollLeft - walk;
    };

    calendarContainer.addEventListener("mousedown", handleMouseDown);
    calendarContainer.addEventListener("mouseleave", handleMouseLeave);
    calendarContainer.addEventListener("mouseup", handleMouseUp);
    calendarContainer.addEventListener("mousemove", handleMouseMove);

    return () => {
      calendarContainer.removeEventListener("mousedown", handleMouseDown);
      calendarContainer.removeEventListener("mouseleave", handleMouseLeave);
      calendarContainer.removeEventListener("mouseup", handleMouseUp);
      calendarContainer.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const getTaskClassName = (taskEndDate, status) => {
    const today = new Date();
    const endDate = parseDateString(taskEndDate);

    if (status === "1") {
      return "task-slot-completed";
    }
    else if (status === "2") {
      return "task-slot-cancelled";
    }
    else {
      if (endDate < today) {
        return "task-slot-passed";
      }
      else if (
        endDate >= today &&
        endDate <=
        new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3)
      ) {
        return "task-slot-closing";
      } else {
        return "task-slot";
      }
    }
  };

  const getTimeHolderClassName = (taskEndDate, status) => {
    const today = new Date();
    const endDate = parseDateString(taskEndDate);

    if (status === "1") {
      return "task-slot-time-holder-completed";
    }
    else if (status === "2") {
      return "task-slot-time-holder-cancelled";
    }
    else {
      if (endDate < today) {
        return "task-slot-time-holder-passed";
      } else if (
        endDate >= today &&
        endDate <=
        new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3)
      ) {
        return "task-slot-time-holder-closing";
      } else {
        return "task-slot-time-holder";
      }
    }
  };

  const getPlayStopClassName = (taskEndDate, status) => {
    const today = new Date();
    const endDate = parseDateString(taskEndDate);

    if (status === "1") {
      return "task-slot-play-completed";
    }
    else if (status === "2") {
      return "task-slot-play-cancelled";
    }
    else {
      if (endDate < today) {
        return "task-slot-play-passed";
      } else if (
        endDate >= today &&
        endDate <=
        new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3)
      ) {
        return "task-slot-play-closing";
      } else {
        return "task-slot-play";
      }
    }
  };

  useEffect(() => {
    const calendarContainer = calendarRef.current;

    if (calendarContainer) {
      const today = new Date();
      if (
        today.getMonth() === selectedMonth &&
        today.getFullYear() === selectedYear
      ) {
        const dayIndex = today.getDate() - 1;
        const scrollPosition = dayIndex * 100;
        calendarContainer.scrollLeft = scrollPosition;
      }
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    setBoardTitle(board.title);
    if (propTasks?.length > 0) {
      setTasks(propTasks);
    } else {
      setTasks([]);
    }
  }, [propTasks, board, panels]);

  const hasWritePermission =
    projectAuthorization?.authorizations === "all" ||
    projectAuthorization?.authorizations?.panels === "all" ||
    projectAuthorization?.authorizations?.panels?.some(
      (auth) => auth.id === selectedPanelId && auth.authorization !== "read"
    );

  return (
    <>
      <div className="task-manager-header-section">
        <div className="month-selector">
          <div
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              display: "flex",
            }}
          >
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Dropdown
                style={{ width: "120px" }}
                options={monthNamesDDL}
                onChange={handleMonthChange}
                value={selectedMonth}
              />

              <Dropdown
                style={{ marginLeft: "15px", width: "120px" }}
                options={yearsDDL}
                onChange={handleYearChange}
                value={selectedYear}
              />
            </div>
          </div>

          {hasWritePermission ? (
            <div
              onClick={() => setIsEditing(true)}
              style={{ cursor: "var(--pointer)" }}
            >
              {isEditing ? (
                <input
                  className="task-manager-title-input"
                  type="text"
                  value={boardTitle}
                  onChange={(e) => setBoardTitle(e.target.value)}
                  onBlur={() => handleTitleUpdate()}
                  autoFocus
                  maxLength={30}
                />
              ) : (
                <span className="task-manager-title">
                  {boardTitle} <TbEdit size={18} />
                </span>
              )}
            </div>
          ) : (
            <span className="task-manager-title">{boardTitle}</span>
          )}
        </div>

        <div className="task-manager-header-button-container">
          {hasWritePermission && (
            <button
              onClick={() => setIsAlertOpen(true)}
              className="remove-board-button"
            >
              {getTranslation("removeBoard")}
              <TbClipboardText />
            </button>
          )}

          <button className="today-task-button" onClick={openTodayTasksModal}>
            {getTranslation("todayTasks")} <TbClipboardText />
          </button>

          {hasWritePermission && (
            <button onClick={openNewTaskModal} className="new-task-button">
              {getTranslation("newTask")}
              <TbClipboardText />
            </button>
          )}
        </div>
      </div>

      <div style={{ maxHeight: "80%", height: "80%" }}>
        <div className="calendar-container" ref={calendarRef}>
          <TaskDetailsModal
            isOpen={isTaskDetailsModalOpen}
            onClose={closeTaskDetailsModal}
            task={selectedTask}
            projectAuthorization={projectAuthorization}
          />

          <div className="calendar-grid">
            {daysInMonth.map((dayData, index) => (
              <div key={index} className="calendar-day">
                <div className="day-name">
                  <div style={{ fontSize: "16px", fontWeight: "600" }}>
                    {dayData.date}
                  </div>
                  <div style={{ fontSize: "11px" }}>{dayData.dayName}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="task-container">
            {propTasks?.map((task, taskIndex) => {
              const taskStartDay =
                parseDateString(task.taskStart).getMonth() === selectedMonth
                  ? parseDateString(task.taskStart).getDate()
                  : 1;

              const taskEndDay =
                parseDateString(task.taskEnd).getMonth() === selectedMonth
                  ? parseDateString(task.taskEnd).getDate()
                  : daysInSelectedMonth;

              const startIndex = taskStartDay - 1;
              const spanDays = taskEndDay - taskStartDay + 1;
              return (
                <React.Fragment key={taskIndex}>
                  <div
                    className={getTaskClassName(task.taskEnd, task.taskStatus)}
                    style={{
                      gridColumn: `${startIndex + 1} / span ${spanDays}`,
                    }}
                    onClick={() => openModalWithTask(task)}
                  >
                    <div>
                      <span>{task.taskTitle}</span>
                      <span style={{ fontSize: "11px" }}>{task.subTitle}</span>
                    </div>

                    <span style={{ flexDirection: 'row', display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'center', marginLeft: '15px' }}>
                      <div
                        className={getTimeHolderClassName(
                          task.taskEnd,
                          task.taskStatus
                        )}
                      >
                        {parseDateString(task.taskStart).getDate()}{" "}{monthNames[parseDateString(task.taskStart).getMonth()]}-{" "}
                        {parseDateString(task.taskEnd).getDate()}{" "}
                        {monthNames[parseDateString(task.taskEnd).getMonth()]}
                      </div>

                      {activeKronos && activeKronos?.taskId === task.taskId ?
                        <div
                          className={getPlayStopClassName(
                            task.taskEnd,
                            task.taskStatus
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            stopKronos();
                          }}
                        >
                          <TbPlayerStop size={16} />
                        </div>
                        :
                        <div
                          className={getPlayStopClassName(
                            task.taskEnd,
                            task.taskStatus
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateStartKronos(false, task);
                          }}
                        >
                          <TbPlayerPlay size={16} />
                        </div>
                      }

                    </span>

                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {isTodaysTaskModalOpen && (
        <TodaysTaskModal
          tasks={todayTasks}
          onClose={closeTodayTasksModal}
          openDetailModal={openModalWithTask}
        />
      )}

      <AddTaskModal
        isOpen={isNewTaskModalOpen}
        selectedPanelId={selectedPanelId}
        boardId={board.id}
        onClose={closeNewTaskModal}
      />

      <AlertBox
        isOpen={isAlertOpen}
        title={getTranslation("areYouSure")}
        message={getTranslation("boardDeleteWarning")}
        onConfirm={handleConfirmDeleteBoard}
        onCancel={handleCancelDeleteBoard}
        confirmText={getTranslation("yes")}
        cancelText={getTranslation("no")}
        type={"warning"}
      />

      <AlertBox
        isOpen={isKronosAlertOpen}
        message={getTranslation("activeKronosMessage", {
          projectTitle: activeKronos?.projectTitle,
          kronosTitle: activeKronos?.title,
        })}
        buttons={buttons}
        type={"warning"}
      />
    </>
  );
};

export default Calendar;
